import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  HeadingText,
  InputStatusMessage,
} from '@leagueplatform/genesis-core';
import {
  EntityFormBanner,
  HintText,
  inputStatusIconLabelMap,
} from '@web-config-app/core-react-ui';
import { EntityHeadingProps } from '../../types/components';

/**    
 * 
This heading is shared between the Object Compound Layout and the Array of Objects Layout. It appears at the top of every new "page" within the property editor.
 * 
*/

export const CompoundComplexHeading = ({
  label,
  hint,
  banner,
  inputStatus,
  statusMessage,
  hideLabel,
  compactHeading,
}: EntityHeadingProps) => {
  const { formatMessage } = useIntl();
  const showBanner = banner?.title || banner?.description;

  /**
   * When `compactHeading` is set, match the small heading rendered by
   * primitive-object sections (CompoundPrimitiveGroupFieldset), so a complex
   * section's heading visually aligns with a sibling simple section.
   */
  const headingProps = compactHeading
    ? ({ size: 'xs', level: 'display', emphasis: 'subtle' } as const)
    : ({ size: 'xl', level: '2' } as const);

  return (
    <>
      {!hideLabel && (
        <Box
          css={{
            borderBottom: '$borderWidths$thin solid $onSurfaceBorderSubdued',
            margin: '1.25rem 0',
          }}
        >
          {showBanner && (
            <EntityFormBanner
              status={banner.status}
              title={banner.title}
              description={banner.description}
              css={{ margin: '$twoAndHalf 0' }}
            />
          )}
          <HeadingText {...headingProps} css={{ paddingBlockEnd: '$half' }}>
            {label}
          </HeadingText>
          {hint && (
            <HintText size="sm" css={{ marginBlockEnd: '$oneAndHalf' }}>
              {hint}
            </HintText>
          )}
        </Box>
      )}
      {inputStatus && (
        <InputStatusMessage
          data-testid="status-message"
          inputStatus={inputStatus}
          statusIconLabel={formatMessage({
            id: inputStatusIconLabelMap[inputStatus],
          })}
          css={{ marginBlockStart: '$half', marginBlockEnd: '$one' }}
        >
          {statusMessage}
        </InputStatusMessage>
      )}
    </>
  );
};
